import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {InspectionPointsContext, SET_INSPECTION_POINTS_LIST,} from './context/context'
import InputText from '../../common/InputText'
import InspectionPointsTable from './InspectionPointsTable'
import GenerateWiresButton from '../../common/GenerateWiresButton'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const InspectionPointsList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(InspectionPointsContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/inspection_points/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.inspectionPoints' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getInspectionPointsData()
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/inspection_points/?vehicle=${vehicle}&search=${search}`
    getInspectionPointsData()
  }, [debouncedSearch])

  const getInspectionPointsData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'inspectionPoints.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_INSPECTION_POINTS_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'inspectionPoints.loadError' }))
    }
  }

  const buildUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })
    return `/api/inspection_points/?${params.toString()}`
  }

  const deleteInspectionPoint = async (record) => {
    let res = await deleteRequest(
      `/api/inspection_points/${record.id}/?vehicle=${vehicle}`
    )
    if (res?.status) {
      const { currentPage, totalCount, totalPages } = pagination
      const pageSize = 10
      const newTotalCount = totalCount - 1
      const newTotalPages = Math.max(1, Math.ceil(newTotalCount / pageSize))
      const pageToFetch = Math.min(currentPage, newTotalPages)
      const offset = (pageToFetch - 1) * pageSize

      getInspectionPointsData(
        buildUrl({
          vehicle: vehicle,
          search: search,
          limit: pageSize,
          offset: offset,
        })
      )
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'inspectionPoints.deleteSuccess' }),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'inspectionPoints.deleteError' }),
        },
      })
    }
  }

  const confirmDeleteInspectionPoint = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'inspectionPoints.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'inspectionPoints.confirmDeleteContent',
      }),
      onOk: () => {
        deleteInspectionPoint(record)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.inspection_points_list?.length) return []

    return state?.inspection_points_list?.map((item, index) => {
      const points = Array.isArray(item.inspection_points)
        ? item.inspection_points.join(', ')
        : item.inspection_points || '-'

      const harness = item.harness_data?.harness?.length > 0
        ? item.harness_data.harness.join(', ')
        : '-'

      return {
        ...item,
        key: `inspection-point-${index}`,
        rowKey: `inspection-point-${item.id}`,
        index: index,
        inspection_points_display: points,
        harness_display: harness,
      }
    })
  }, [state.inspection_points_list])

  return (
    <>
      {contextHolder}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_inspection_points"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'inspectionPoints.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-inspection-points"
                onChange={(value) => {
                  setSearch(value)
                }}
              />

              <Space size="middle" align="center">
                <GenerateWiresButton
                  is_inspection_points_wire={true}
                  onSuccess={() => getInspectionPointsData()}
                />
              </Space>
            </Flex>
          </Form>

          <Flex vertical gap={16} style={{ width: '100%' }}>
            <InspectionPointsTable
              dataSource={dataSource}
              pagination={pagination}
              getInspectionPointsData={getInspectionPointsData}
              onDelete={confirmDeleteInspectionPoint}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default InspectionPointsList
